import {
    type FragmentType,
    getFragmentData,
    graphql
} from '~/resources/graphql'
import { useCookie } from '#app'
import { setCookie } from 'h3'
import type { H3Event } from 'h3'
import type { Language } from '~/resources/graphql/graphql'
import type { Ref } from 'vue'
import { watch } from 'vue'

type SupportedLocale = 'en-US' | 'es-MX' | 'fr-FR' | 'de-DE'
type LanguageState = {
    defaultLocale: SupportedLocale | null
    supportedLocales: SupportedLocale[]
}
const FALLBACK_LOCALE = 'en-US'
const LOCALE_COOKIE_NAME = 'cpx_locale'

export const UseLanguageOrganizationFragment = graphql(`
    fragment UseLanguageOrganizationFragment on Organization {
        enabledLanguages
        defaultLanguage
    }
`)

function mapLanguageToLocale(language: Language): SupportedLocale | null {
    switch (language) {
        case 'ENGLISH':
            return 'en-US'
        case 'SPANISH_MX':
            return 'es-MX'
        case 'FRENCH':
            return 'fr-FR'
        case 'GERMAN':
            return 'de-DE'
        default:
            return null
    }
}

function synchronizeOrganizationLocale(
    languageState: LanguageState,
    currentLocale: string,
    rememberedLocale: Ref<SupportedLocale | undefined>,
    requestEvent: H3Event | undefined,
    setCurrentLocale: (locale: SupportedLocale) => void
) {
    if (languageState.defaultLocale === null) {
        return
    }

    const nextLocale = languageState.supportedLocales.includes(
        currentLocale as SupportedLocale
    )
        ? (currentLocale as SupportedLocale)
        : languageState.defaultLocale
    const nextCookieLocale =
        rememberedLocale.value !== undefined &&
        !languageState.supportedLocales.includes(rememberedLocale.value)
            ? languageState.defaultLocale
            : nextLocale

    // Nuxt i18n can seed from Accept-Language before organization data exists.
    // Once the org is loaded, persist only an org-supported locale so SSR,
    // hydration, and reloads do not keep using an unsupported browser language.
    if (rememberedLocale.value !== nextCookieLocale) {
        rememberedLocale.value = nextCookieLocale
    }

    if (process.server && requestEvent !== undefined) {
        // Nuxt i18n has already done browser detection by this point, so write
        // the validated org-supported locale onto the SSR response cookie that
        // future requests will use.
        setCookie(requestEvent, LOCALE_COOKIE_NAME, nextCookieLocale, {
            path: '/',
            sameSite: 'lax'
        })
    }

    if (currentLocale !== nextLocale) {
        setCurrentLocale(nextLocale)
    }
}

export function provideLanguageOrganizationFragment(
    organization: Ref<FragmentType<
        typeof UseLanguageOrganizationFragment
    > | null>,
    locale: Ref<string>,
    requestEvent: H3Event | undefined
) {
    const rememberedLocale = useCookie<SupportedLocale | undefined>(
        LOCALE_COOKIE_NAME
    )

    watch(
        organization,
        nextOrganization => {
            if (nextOrganization === null) {
                return
            }

            const organizationData = getFragmentData(
                UseLanguageOrganizationFragment,
                nextOrganization
            )
            const defaultLocale =
                mapLanguageToLocale(organizationData.defaultLanguage) ??
                FALLBACK_LOCALE
            const supportedLocales = organizationData.enabledLanguages
                .map(mapLanguageToLocale)
                .filter((locale): locale is SupportedLocale => locale !== null)

            const languageState = {
                defaultLocale,
                // Always allow the configured default even if enabledLanguages is misconfigured.
                supportedLocales: Array.from(
                    new Set([...supportedLocales, defaultLocale])
                )
            }

            synchronizeOrganizationLocale(
                languageState,
                locale.value,
                rememberedLocale,
                requestEvent,
                nextLocale => {
                    // Set the i18n locale synchronously so layout-level GraphQL
                    // queries read the org-supported locale before choosing translated content.
                    locale.value = nextLocale
                }
            )
        },
        {
            // The app-level context fills this ref immediately after the
            // organization query resolves; synchronous flush keeps layout
            // language selection ordered behind this clamp.
            flush: 'sync',
            immediate: true
        }
    )
}
